import type { BackendId, ExperienceDescriptor, MarkerContent, MarkerLibrary } from "./types";

/** Expected failures (invalid descriptor, bad fetch) are returned, not thrown. */
export type Result<T, E = string> = { ok: true; value: T } | { ok: false; error: E };

/** The minimal fetch surface the loader needs (ISP) — easy to fake in tests. */
export interface FetchLike {
  (url: string): Promise<{ ok: boolean; status: number; json(): Promise<unknown> }>;
}

const BACKEND_IDS: readonly BackendId[] = ["webxr", "gyro", "slam", "image"];
/**
 * Ceiling on positioned layers. Well above any authored composition — Studio's own editor is a
 * hand-placement UI — and low enough that the worst case is a handful of media elements rather
 * than an unbounded fan-out. Exported so Studio enforces the same number rather than a copy.
 */
export const MAX_MARKER_LAYERS = 16;

/**
 * Ceiling on posters in one library. Detection sweeps every entry that is not already tracking, so
 * the cost of a miss grows with N on the oldest supported phone; start() also builds one pipeline,
 * one video element and one GPU surface per entry. Twenty covers a campaign without turning a
 * hand-authored (or attacker-supplied) descriptor into an unbounded fan-out. Exported so Studio
 * enforces the same number rather than a copy of it.
 */
export const MAX_MARKER_LIBRARY_ENTRIES = 20;

/**
 * Ceiling on layers across a whole library, not per entry. `MAX_MARKER_LAYERS` bounds one poster;
 * twenty posters each at that ceiling would validate and then ask start() for hundreds of media
 * elements and GPU surfaces at once. This is the number that actually bounds the fan-out.
 */
export const MAX_LIBRARY_LAYERS = 32;

const MARKER_VIDEO_FITS = ["exact", "cover"];

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

export interface ValidateDescriptorOptions {
  /**
   * Accept `blob:` asset URLs alongside relative/http(s). Off by default: this validator is the
   * single gate for every descriptor entry point (see `validateDescriptor` below), including an
   * attacker-influenceable `?descriptor=` query string, and a *fetched* descriptor.json can never
   * legitimately carry a blob: URL — its bytes would already have to be in the tab. `blob:`'s
   * bytes do live only in the tab that called `URL.createObjectURL` and are never embedded in the
   * URL string itself (unlike `data:`), so accepting one is safe in principle — but only the
   * studio's own live-preview composer, which builds a descriptor from blob: URLs it just created
   * itself, has a reason to opt in.
   */
  allowBlobUrls?: boolean;
}

/**
 * A non-empty URL string that is relative or http(s) — or, with `allowBlobUrls`, also blob:.
 * Rejects `javascript:`/`data:` and other schemes — `usdzUrl` becomes an `<a href>` we click on
 * iOS, so a bad scheme is a navigation/XSS sink (descriptors are attacker-influenceable via
 * `?descriptor=`).
 */
const isSafeAssetUrl = (value: unknown, options: ValidateDescriptorOptions): boolean => {
  if (!isNonEmptyString(value)) return false;
  try {
    const url = new URL(value, "https://visorxr.invalid/");
    if (url.protocol === "http:" || url.protocol === "https:") return true;
    return options.allowBlobUrls === true && url.protocol === "blob:";
  } catch {
    return false;
  }
};

const fail = (error: string): Result<never> => ({ ok: false, error });

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;

const MARKER_LAYER_TYPES = ["video", "image"];
const LAYER_RECT_FIELDS = ["x", "y", "width", "height"] as const;
/** Fields that must additionally be > 0 — x/y may be zero, negative, or beyond [0,1] (a layer can
 *  legitimately sit outside the marker's own bounds, e.g. bleeding art off the poster's edge). */
const LAYER_RECT_POSITIVE_FIELDS = ["width", "height"] as const;

/**
 * One `layers[i]`'s own shape, split out for the same reason `validateMarkerContent` is.
 * `ownerPath` is the containing marker's path (`descriptor.marker`, or a library entry's) so a
 * 20-poster campaign's error names the poster that is wrong, not just the field.
 */
function validateMarkerLayer(
  rawLayer: unknown,
  index: number,
  ownerPath: string,
  options: ValidateDescriptorOptions,
): Result<never> | null {
  const prefix = `${ownerPath}.layers[${index}]`;

  // `layers: [null]` is valid JSON, and a cast is not a check — the first property read would
  // throw a TypeError out of a function whose contract is to return a Result.
  if (typeof rawLayer !== "object" || rawLayer === null || Array.isArray(rawLayer)) {
    return fail(`${prefix} must be an object`);
  }
  const layer = rawLayer as Record<string, unknown>;

  if (!MARKER_LAYER_TYPES.includes(layer.type as string)) {
    return fail(`${prefix}.type must be "video" or "image"`);
  }
  if (!isSafeAssetUrl(layer.src, options)) {
    return fail(`${prefix}.src must be a relative or http(s) URL string`);
  }

  const rect = layer.rect as Record<string, unknown> | undefined;
  if (typeof rect !== "object" || rect === null) {
    return fail(`${prefix}.rect must be an object`);
  }
  for (const field of LAYER_RECT_FIELDS) {
    const value = rect[field];
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return fail(`${prefix}.rect.${field} must be a finite number`);
    }
  }
  for (const field of LAYER_RECT_POSITIVE_FIELDS) {
    if ((rect[field] as number) <= 0) {
      return fail(`${prefix}.rect.${field} must be a positive number`);
    }
  }
  if (rect.rotationDeg !== undefined && (typeof rect.rotationDeg !== "number" || !Number.isFinite(rect.rotationDeg))) {
    return fail(`${prefix}.rect.rotationDeg must be a finite number when provided`);
  }

  return null;
}

/**
 * Everything a tracked marker plays, minus the target that identifies it — the half a standalone
 * `marker` and a library entry share, so the two shapes can never drift apart in what they accept.
 * `path` is where this content sits in the descriptor, and prefixes every message it returns.
 */
function validateMarkerContent(
  content: Record<string, unknown>,
  path: string,
  options: ValidateDescriptorOptions,
): Result<never> | null {
  const hasVideo = content.videoUrl !== undefined;
  const hasImage = content.imageUrl !== undefined;
  if (hasVideo && hasImage) {
    return fail(`${path} sets both videoUrl and imageUrl — set exactly one base content field`);
  }
  if (!hasVideo && !hasImage) {
    return fail(`${path}.videoUrl or ${path}.imageUrl must be set — exactly one base content field is required`);
  }
  if (hasVideo && !isSafeAssetUrl(content.videoUrl, options)) {
    return fail(`${path}.videoUrl must be a relative or http(s) URL string`);
  }
  if (hasImage && !isSafeAssetUrl(content.imageUrl, options)) {
    return fail(`${path}.imageUrl must be a relative or http(s) URL string`);
  }
  if (
    content.widthMeters !== undefined &&
    (typeof content.widthMeters !== "number" || !Number.isFinite(content.widthMeters) || content.widthMeters <= 0)
  ) {
    // NaN and Infinity are numbers that pass `<= 0`, and would cross this boundary as apparently
    // valid physical dimensions.
    return fail(`${path}.widthMeters must be a finite positive number when provided`);
  }
  if (content.videoFit !== undefined && !MARKER_VIDEO_FITS.includes(content.videoFit as string)) {
    return fail(`${path}.videoFit must be "exact" or "cover" when provided`);
  }
  if (content.layers !== undefined) {
    if (!Array.isArray(content.layers)) {
      return fail(`${path}.layers must be an array when provided`);
    }
    // Bounded before anything iterates it. `?descriptor=` is attacker-influenceable, and
    // ImageBackend.start() begins one media load per entry, builds one GPU surface per success,
    // and walks every surface on every tracking frame — so an unbounded array is a network,
    // decoder and per-frame budget all at once, on the phone this runs on.
    if (content.layers.length > MAX_MARKER_LAYERS) {
      return fail(`${path}.layers must contain at most ${MAX_MARKER_LAYERS} entries`);
    }
    for (let i = 0; i < content.layers.length; i++) {
      const layerError = validateMarkerLayer(content.layers[i], i, path, options);
      if (layerError) return layerError;
    }
  }
  return null;
}

/** `marker`'s own shape, split out because `validateDescriptor` already reads long top to bottom. */
function validateMarker(marker: Record<string, unknown>, options: ValidateDescriptorOptions): Result<never> | null {
  if (!isSafeAssetUrl(marker.targetUrl, options)) {
    return fail("descriptor.marker.targetUrl must be a relative or http(s) URL string");
  }
  return validateMarkerContent(marker, "descriptor.marker", options);
}

/** `markerLibrary`'s own shape: one shared target plus the per-entry content it identifies. */
function validateMarkerLibrary(
  library: Record<string, unknown>,
  options: ValidateDescriptorOptions,
): Result<never> | null {
  if (!isSafeAssetUrl(library.targetUrl, options)) {
    return fail("descriptor.markerLibrary.targetUrl must be a relative or http(s) URL string");
  }
  if (!Array.isArray(library.entries)) {
    return fail("descriptor.markerLibrary.entries must be an array");
  }
  // An empty library would fetch and decode a compiled target that nothing then tracks — the
  // silent-no-op shape a descriptor check exists to catch at authoring time.
  if (library.entries.length === 0) {
    return fail("descriptor.markerLibrary.entries must contain at least one entry");
  }
  // Detection runs across every entry that is not already tracking, so cost grows with N on the
  // oldest phone in the support matrix. The ceiling is what keeps a bad descriptor from turning
  // into an unbounded per-frame matching sweep.
  if (library.entries.length > MAX_MARKER_LIBRARY_ENTRIES) {
    return fail(`descriptor.markerLibrary.entries must contain at most ${MAX_MARKER_LIBRARY_ENTRIES} entries`);
  }
  let totalLayers = 0;
  for (let i = 0; i < library.entries.length; i++) {
    const path = `descriptor.markerLibrary.entries[${i}]`;
    const entry = asRecord(library.entries[i]);
    if (entry === undefined) return fail(`${path} must be an object`);
    const entryError = validateMarkerContent(entry, path, options);
    if (entryError) return entryError;
    totalLayers += Array.isArray(entry.layers) ? entry.layers.length : 0;
  }
  if (totalLayers > MAX_LIBRARY_LAYERS) {
    return fail(
      `descriptor.markerLibrary must carry at most ${MAX_LIBRARY_LAYERS} layers in total, not ${totalLayers}`,
    );
  }
  return null;
}

/**
 * Validate an unknown value as an ExperienceDescriptor. This is the single source of truth for
 * descriptor shape — every entry point (URL load, inline config) flows through here.
 */
export function validateDescriptor(
  input: unknown,
  options: ValidateDescriptorOptions = {},
): Result<ExperienceDescriptor> {
  if (typeof input !== "object" || input === null) return fail("descriptor must be an object");
  const d = input as Record<string, unknown>;

  if (!isNonEmptyString(d.id)) return fail("descriptor.id must be a non-empty string");
  if (!isNonEmptyString(d.title)) return fail("descriptor.title must be a non-empty string");

  // Narrowed before use: a bare cast lets `"marker": null` through to validateMarker, where the
  // first property read throws — turning malformed external input into an exception on a path
  // whose whole contract is to return a Result.
  const marker = d.marker === undefined ? undefined : asRecord(d.marker);
  if (d.marker !== undefined && marker === undefined) {
    return fail("descriptor.marker must be an object when provided");
  }
  if (marker !== undefined) {
    const markerError = validateMarker(marker, options);
    if (markerError) return markerError;
  }

  const markerLibrary = d.markerLibrary === undefined ? undefined : asRecord(d.markerLibrary);
  if (d.markerLibrary !== undefined && markerLibrary === undefined) {
    return fail("descriptor.markerLibrary must be an object when provided");
  }
  // One experience, one content path. Both present is ambiguous about which target file loads and
  // which entry index means what, and `resolveMarkerLibrary` would have to silently pick a winner.
  if (marker !== undefined && markerLibrary !== undefined) {
    return fail("descriptor carries both marker and markerLibrary — set exactly one");
  }
  if (markerLibrary !== undefined) {
    const libraryError = validateMarkerLibrary(markerLibrary, options);
    if (libraryError) return libraryError;
  }
  // Either shape is a complete image-target content path; the rules below only care whether one
  // is present, never which.
  const hasMarkerContent = marker !== undefined || markerLibrary !== undefined;

  // Without marker content, glTF-on-canvas is the only content path, so model/placement are
  // required. With it, they become optional (a poster-only experience ships no glTF) but are still
  // validated in full whenever present — a marker doesn't relax the rules for a model it carries.
  const model = d.model as Record<string, unknown> | undefined;
  if (model !== undefined || !hasMarkerContent) {
    if (!model || !isNonEmptyString(model.url)) {
      return fail("descriptor.model.url must be a non-empty string");
    }
    if (model.usdzUrl !== undefined && !isSafeAssetUrl(model.usdzUrl, options)) {
      return fail("descriptor.model.usdzUrl must be a relative or http(s) URL string when provided");
    }
  }

  // model and placement are one contract (what to place, and where) — a marker makes the *pair*
  // optional, never one half of it. Requiring placement whenever model is given (in addition to
  // whenever there's no marker) is what stops a hybrid descriptor from shipping a glTF nothing can
  // place — main.ts loads it, but no backend's update() ever positions it.
  const placement = d.placement as Record<string, unknown> | undefined;
  if (placement !== undefined || !hasMarkerContent || model !== undefined) {
    if (!placement || placement.mode !== "floor-elevated") {
      return fail('descriptor.placement.mode must be "floor-elevated"');
    }
    if (typeof placement.distance !== "number" || placement.distance <= 0) {
      return fail("descriptor.placement.distance must be a positive number");
    }
    if (typeof placement.height !== "number" || placement.height < 0) {
      return fail("descriptor.placement.height must be a non-negative number");
    }
  }
  // The mirror of the rule above, and the half that was missing: a placement with nothing to place
  // still satisfies gyro/webxr's start() precondition, so the fallback starts, reports tracking,
  // and renders an empty camera feed — the failure mode a descriptor check exists to prevent.
  if (placement !== undefined && model === undefined) {
    return fail("descriptor.placement is set but descriptor.model is not — they are one contract");
  }

  const prefer = (d.tracking as Record<string, unknown> | undefined)?.prefer;
  if (!Array.isArray(prefer) || prefer.length === 0) {
    return fail("descriptor.tracking.prefer must be a non-empty array");
  }
  for (const id of prefer) {
    if (!BACKEND_IDS.includes(id as BackendId)) {
      return fail(`descriptor.tracking.prefer contains unknown backend "${String(id)}"`);
    }
  }
  // Without this, main.ts's dynamic-import gate (which fires only when descriptor.marker is
  // present, so glTF-only experiences pay zero image-tracker bytes) would silently never start
  // the backend the author asked for — "image" would sit in prefer, matched by nothing, ever.
  if (prefer.includes("image") && !hasMarkerContent) {
    return fail('descriptor.tracking.prefer includes "image" but neither descriptor.marker nor descriptor.markerLibrary is set');
  }
  // The dual of the check above: a marker that no preference ever asks for is downloaded for
  // nothing (main.ts's import gate reads descriptor.marker directly, not prefer) and never tracked.
  if (hasMarkerContent && !prefer.includes("image")) {
    return fail(
      `${marker !== undefined ? "descriptor.marker" : "descriptor.markerLibrary"} is set but ` +
        'descriptor.tracking.prefer never lists "image"',
    );
  }
  // WebXR's requestSession requires transient user activation, and activation does not survive a
  // promise chain: by the time an earlier backend has awaited camera permission, a dynamic import
  // and a target fetch before failing, the Start tap is long spent and requestSession rejects with
  // SecurityError. Listing webxr after an async-starting backend therefore describes a fallback
  // that cannot happen — caught here as an authoring error rather than failing silently at runtime.
  const webxrIndex = prefer.indexOf("webxr");
  const activationSpenders = prefer.filter((id) => id === "image" || id === "gyro");
  if (webxrIndex > 0 && activationSpenders.length > 0 && prefer.indexOf(activationSpenders[0]) < webxrIndex) {
    return fail(
      'descriptor.tracking.prefer lists "webxr" after a backend that starts asynchronously — WebXR ' +
        "needs the Start tap's user activation, which is gone by then. List \"webxr\" first.",
    );
  }
  // GyroBackend/WebXRBackend both place a floor-elevated object and throw a raw developer-facing
  // error if asked to start without one — catching the missing pairing here turns that into a
  // descriptor-authoring error instead of an end-user-visible crash.
  const needsPlacement = prefer.includes("gyro") || prefer.includes("webxr");
  if (needsPlacement && placement === undefined) {
    const culprit = prefer.includes("gyro") ? "gyro" : "webxr";
    return fail(`descriptor.tracking.prefer includes "${culprit}" but descriptor.placement is not set`);
  }

  return { ok: true, value: input as ExperienceDescriptor };
}

/** Fetch a descriptor JSON and validate it. `fetchFn` is injected so this needs no real network. */
/**
 * The one shape consumers read. A descriptor expresses image-target content two ways — one
 * `marker`, or a `markerLibrary` of many — and every consumer wants the same thing from both:
 * which compiled target to fetch, and what plays on entry `i`. Wrapping the single marker here
 * means the player has one code path rather than a branch at every use, and a single-marker
 * experience is simply a library of one.
 *
 * Returns null when the descriptor carries no image-target content at all (a glTF-only
 * experience), which is also the predicate `main.ts` gates the tracker's dynamic import on.
 * Expects an already-validated descriptor: `validateDescriptor` refuses both fields at once.
 */
export function resolveMarkerLibrary(descriptor: ExperienceDescriptor): MarkerLibrary | null {
  if (descriptor.markerLibrary) return descriptor.markerLibrary;
  if (!descriptor.marker) return null;

  // Destructured rather than spread: `targetUrl` belongs to the library, not the entry, and
  // copying it into the entry would invent a per-entry target the schema does not have.
  const { targetUrl, ...content } = descriptor.marker;
  return { targetUrl, entries: [content] };
}

/**
 * Which of `MarkerContent`'s two base-content fields is set, and its URL. Expects
 * already-validated content: `validateMarkerContent` refuses both or neither, so this throws
 * only on a caller mistake, not on attacker-influenceable input.
 */
export function resolveBaseContent(content: Pick<MarkerContent, "videoUrl" | "imageUrl">): {
  kind: "video" | "image";
  url: string;
} {
  if (content.videoUrl !== undefined) return { kind: "video", url: content.videoUrl };
  if (content.imageUrl !== undefined) return { kind: "image", url: content.imageUrl };
  throw new Error("MarkerContent has neither videoUrl nor imageUrl — pass content validateDescriptor has already accepted");
}

export async function loadDescriptor(
  url: string,
  fetchFn: FetchLike,
): Promise<Result<ExperienceDescriptor>> {
  let response: Awaited<ReturnType<FetchLike>>;
  try {
    response = await fetchFn(url);
  } catch (error) {
    return fail(`descriptor fetch failed: ${(error as Error).message}`);
  }

  if (!response.ok) return fail(`descriptor fetch failed: HTTP ${response.status}`);

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return fail("descriptor body is not valid JSON");
  }

  return validateDescriptor(body);
}
